import * as React from "react";
import { ShieldCheck, FileText, Lock, UserCheck, BellRing, Mail, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function PrivacyPolicy() {
  const sections = [{
    id: "information-collected",
    icon: FileText,
    title: "Information Collected During Estimates",
    description: "When you request an estimate or schedule a consultation with Decatur Heritage Renovations, we collect only the specific details required to evaluate your remodeling project accurately:",
    points: ["Full name and preferred contact details (email address and phone number)", "Project descriptions, scope notes, and design preferences", "Target project schedules, timelines, and anticipated start dates", "Approximate room dimensions, layout details, and structural notes"],
    note: "We do not request or store physical street addresses on this public submission portal prior to in-person consultation confirmations."
  }, {
    id: "data-security",
    icon: Lock,
    title: "How Data Is Handled and Secured",
    description: "We take data protection seriously. All details submitted through our website forms and consultation requests are transmitted through encrypted connections and stored in access-restricted business environments.",
    points: ["Encrypted data transmission across all online intake channels", "Strictly limited internal access restricted to designated project managers", "Information is retained only for the duration necessary to deliver estimates and coordinate agreed project plans"],
    note: null
  }, {
    id: "no-sharing-commitment",
    icon: ShieldCheck,
    title: "No Selling or Renting of Personal Information",
    description: "We respect your privacy and will never monetize your data. Decatur Heritage Renovations makes an explicit commitment regarding your personal details:",
    points: ["We never sell, rent, lease, or trade your personal information to third parties", "We do not supply customer data to external marketing agencies or lead networks", "Data is shared solely with necessary technical subcontractors directly involved with your specific scope of work"],
    note: null
  }, {
    id: "communications",
    icon: BellRing,
    title: "Communication Preferences and Opt-Outs",
    description: "We communicate with you exclusively regarding your direct inquiries, consultation scheduling, and project updates:",
    points: ["Direct email and telephone follow-ups regarding requested estimates", "Text message confirmations for scheduled consultations upon consent", "You may update communication preferences or opt out of non-essential notices at any time by contacting us directly"],
    note: null
  }];
  return <section id="privacypolicy" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium tracking-wide mb-4">
            <UserCheck className="size-3.5 text-chart-1" />
            <span>Client Data &amp; Trust</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            This policy outlines how Decatur Heritage Renovations gathers, manages, and safeguards information provided during consultation and estimate requests.
          </p>
        </div>

        {/* Policy Content Grid */}
        <div className="space-y-8">
          {sections.map((sec, index) => {
          const IconComp = sec.icon;
          return <Card key={sec.id} data-index={index} className="bg-card/90 backdrop-blur-xs border border-border shadow-xs hover:shadow-md transition-shadow rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-start gap-4 pb-2">
                  <div className="p-2.5 rounded-lg bg-secondary text-foreground mt-0.5 shrink-0">
                    <IconComp className="size-5 text-chart-1" />
                  </div>
                  <div className="space-y-1">
                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                      {sec.title}
                    </CardTitle>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1">
                      {sec.description}
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-3 pb-6 sm:pl-16 space-y-4">
                  <ul className="grid gap-2.5 sm:grid-cols-1">
                    {sec.points.map((pt, pIdx) => <li key={pIdx} className="flex items-start gap-3 text-sm sm:text-base text-foreground/90 leading-relaxed">
                        <span className="size-1.5 rounded-full bg-chart-1 mt-2 shrink-0" />
                        <span>{pt}</span>
                      </li>)}
                  </ul>
                  {sec.note && <div className="mt-4 p-3 rounded-md bg-secondary/70 border border-border/80 text-xs sm:text-sm text-muted-foreground leading-normal">
                      {sec.note}
                    </div>}
                </CardContent>
              </Card>;
        })}
        </div>

        <Separator className="my-12 md:my-16" />

        {/* Contact & Inquiries Card */}
        <div className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-xs">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 space-y-3">
              <h2 className="font-serif text-2xl font-semibold text-foreground">
                Questions or Preference Changes?
              </h2>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                If you have questions about how your consultation details are managed, or wish to update your communication preferences, contact our team directly.
              </p>
            </div>

            <div className="md:col-span-5 flex flex-col gap-3 sm:justify-end">
              <a href="mailto:serviceleadalerts@gmail.com" className="flex items-center gap-3 p-3.5 rounded-lg bg-secondary/80 hover:bg-secondary border border-border text-foreground transition-colors group">
                <div className="p-2 rounded-md bg-background shrink-0">
                  <Mail className="size-4 text-chart-1" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    Email
                  </span>
                  <span className="block text-sm font-semibold truncate text-foreground group-hover:underline">
                    serviceleadalerts@gmail.com
                  </span>
                </div>
              </a>

              <a href="tel:+14708479076" className="flex items-center gap-3 p-3.5 rounded-lg bg-secondary/80 hover:bg-secondary border border-border text-foreground transition-colors group">
                <div className="p-2 rounded-md bg-background shrink-0">
                  <Phone className="size-4 text-chart-1" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    Telephone
                  </span>
                  <span className="block text-sm font-semibold text-foreground group-hover:underline">
                    (470) 847-9076
                  </span>
                </div>
              </a>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">
              Decatur Heritage Renovations — Privacy Policy
            </span>
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link to="/ContactPage">
                <span>Go to Contact Page</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}